/**
 * Nơi duy nhất biết phiên đăng nhập được lưu ở đâu.
 *
 * **Module này không import gì cả** — có chủ ý. `utils/config.js` (interceptor 401)
 * cần xóa phiên, mà `auth/session.js` lại cần `http` của `utils/config.js`. Nếu hai
 * file đó gọi nhau thì thành vòng import. Đặt phần "sở hữu token" xuống đây, hai
 * file trên đều chỉ import xuống một chiều.
 *
 * Trước đây việc xóa phiên nằm rải ở ba chỗ và mỗi chỗ liệt kê một tập khóa khác
 * nhau: interceptor 401 trong `utils/config.js`, hàm logout trong `HeaderConek`, và
 * cache menu trong `redux/menu/getMenuOfUser.js`. Thiếu một khóa ở một chỗ là dữ
 * liệu của người trước còn sót lại cho người sau đăng nhập vào cùng tab.
 */

/* Khóa cookie — phiên. */
const COOKIE_ACCESS = "access_token";
const COOKIE_TYPE = "type_user";

/* Khóa localStorage — dữ liệu gắn với phiên, phải xóa cùng lúc. */
const LS_KEYS = [
  "permissions",
  "cms.session", // store user (auth/session.js)
  "cms.menu", // store menu (auth/session.js)
  "menu_cache", // legacy: cache menu của slice cũ
  "menu_cache_time", // legacy
  "persist:data", // legacy: redux-persist
];

/* ------------------------------- Cookie thô -------------------------------- */

function readCookie(name) {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const c = part.trimStart();
    if (c.startsWith(prefix)) return c.slice(prefix.length);
  }
  return null;
}

function writeCookie(name, value, days) {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }
  document.cookie = `${name}=${value || ""}${expires}; path=/`;
}

function deleteCookie(name) {
  document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
}

/* --------------------------------- Listener -------------------------------- */

const listeners = new Set();

/* ---------------------------------- API ----------------------------------- */

export const tokens = {
  accessToken: () => readCookie(COOKIE_ACCESS),

  /** ADMIN | SHOP | STAFF | BRANCH_MANAGER | CUSTOMER */
  typeUser: () => readCookie(COOKIE_TYPE),

  isAuthenticated: () => readCookie(COOKIE_ACCESS) !== null,

  /** Sau khi đăng nhập thành công. `permissions` là chuỗi base64 do BE trả. */
  set({ accessToken, typeUser, permissions }, days = 30) {
    writeCookie(COOKIE_ACCESS, accessToken, days);
    writeCookie(COOKIE_TYPE, typeUser, days);
    if (permissions !== undefined && permissions !== null) {
      try {
        localStorage.setItem("permissions", permissions);
      } catch {
        /* storage bị chặn — bỏ qua, phân quyền sẽ rỗng cho tới lần gọi /getUser */
      }
    }
    listeners.forEach((fn) => fn(true));
  },

  /**
   * Xóa sạch phiên. Gọi khi: đăng xuất, hoặc 401 mà không cứu được.
   * Các store đăng ký qua `subscribe` sẽ tự reset — không cần ai gọi tay.
   */
  clear() {
    deleteCookie(COOKIE_ACCESS);
    deleteCookie(COOKIE_TYPE);
    for (const key of LS_KEYS) {
      try {
        localStorage.removeItem(key);
      } catch {
        /* bỏ qua */
      }
    }
    listeners.forEach((fn) => fn(false));
  },

  /** Trả về hàm hủy đăng ký. */
  subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};
